// Master Apps · Auditoria · cobertura documental e artefatos para o auditor.
// A conta de conformidade NÃO vive aqui: é a regra única de @/lib/compliance,
// a mesma consumida pelo acervo de documentos — uma fonte da verdade só.
// Nada nesta tela é ilustrativo: números vêm das aplicações carregadas e os
// artefatos (CSV) são montados no navegador, sob demanda.
import { type CSSProperties, type ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { AppPhoto, CriticalityBadge, VisibilityBadge } from '@/components/app-visuals';
import {
  IconActivity,
  IconAlert,
  IconArrow,
  IconClock,
  IconRefresh,
  IconShield,
} from '@/components/icons';
import { ButtonGhost, Card, Sidebar, Tag, TopBar } from '@/components/primitives';
import { byViewport, useViewport } from '@/hooks/use-viewport';
import type { App } from '@/lib/catalog-config';
import { APP_TYPES, STATUS_TYPES } from '@/lib/catalog-config';
import type { ComplianceCoverage, ComplianceResult } from '@/lib/compliance';
import {
  COMPLIANCE_LABELS,
  COMPLIANCE_LEVEL_LABELS,
  COMPLIANCE_ORDER,
  complianceCheckState,
  complianceColor,
  complianceCoverage,
  complianceOf,
} from '@/lib/compliance';
import type { ScreenProps } from '@/lib/theme';
import {
  fontMono,
  fontSans,
  makePalette,
  NOC_RED,
  numeroTabular,
  rotuloCaixaAlta,
} from '@/lib/theme';

export function CoverageBar({
  value,
  total,
  color,
  dark,
  height = 6,
}: Readonly<{
  value: number;
  total: number;
  color: string;
  dark: boolean;
  height?: number;
}>) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div
      style={{
        height,
        background: dark ? '#26272d' : '#f1f5f4',
        borderRadius: height / 2,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          background: color,
          transition: 'width 600ms',
        }}
      />
    </div>
  );
}

export function AuditDonut({
  ok,
  total,
  dark,
  size = 160,
}: Readonly<{
  ok: number;
  total: number;
  dark: boolean;
  /**
   * Diâmetro em px. Os 160 originais são o padrão, então o desktop não muda; o
   * celular pede um rosco menor porque o cartão inteiro tem 254px úteis em
   * 320px de tela (medido) e ainda precisa da legenda embaixo.
   */
  size?: number;
}>) {
  // Espessura do anel e corpo do percentual em PROPORÇÃO do diâmetro: em 160px
  // dão exatamente os 16px e 36px de antes, e em 140px encolhem juntos em vez de
  // o "100%" encostar na borda interna do anel.
  const thickness = Math.round(size * 0.1);
  const valueSize = Math.round(size * 0.225);
  const r = size / 2 - thickness / 2;
  const c = 2 * Math.PI * r;
  const ratio = total > 0 ? ok / total : 0;
  const okLen = ratio * c;
  const okColor = '#16a34a';
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={dark ? '#26272d' : '#f1f5f4'}
          strokeWidth={thickness}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={okColor}
          strokeWidth={thickness}
          strokeDasharray={`${okLen} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          strokeLinecap="butt"
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            ...numeroTabular,
            fontSize: valueSize,
            fontWeight: 600,
            letterSpacing: '-0.03em',
            lineHeight: 1,
            color: dark ? '#fafaf9' : '#0a0a0b',
          }}
        >
          {(ratio * 100).toFixed(0)}%
        </div>
        <div
          style={{
            ...rotuloCaixaAlta,
            fontSize: 10,
            fontWeight: 600,
            marginTop: 4,
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            color: dark ? '#a8a29e' : '#78716c',
          }}
        >
          {ok}/{total} apps
        </div>
      </div>
    </div>
  );
}

// ── Exportação CSV (sem dependência nova: Blob + link temporário) ────────────
// Separador ';' e BOM UTF-8: é o que o Excel pt-BR abre sem quebrar acento.

const CSV_SEP = ';';

type CsvValue = string | number | null | undefined;

function csvCell(value: CsvValue): string {
  const s = value === null || value === undefined ? '' : String(value);
  return `"${s.replaceAll('"', '""')}"`;
}

function toCsv(rows: CsvValue[][]): string {
  return rows.map((row) => row.map(csvCell).join(CSV_SEP)).join('\r\n');
}

/** Número em pt-BR (vírgula decimal) para o Excel não ler '87.5' como texto. */
function csvNumber(value: number, digits = 1): string {
  return value.toFixed(digits).replace('.', ',');
}

function fileStamp(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** BOM UTF-8 (U+FEFF): sem ele o Excel pt-BR lê os acentos errado. */
const UTF8_BOM = String.fromCodePoint(0xfeff);

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([UTF8_BOM + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

const VISIBILITY_LABELS: Record<string, string> = {
  publico: 'Público',
  privado: 'Privado',
  interno: 'Interno',
};

const CRITICALITY_LABELS: Record<string, string> = {
  alta: 'Alta',
  media: 'Média',
  baixa: 'Baixa',
};

const CHECK_STATE_LABELS = {
  cadastrado: 'Cadastrado',
  dispensado: 'Dispensado',
  ausente: 'Não cadastrado',
} as const;

type AuditRow = { app: App; result: ComplianceResult };

/** Uma linha por aplicação, com a situação de cada um dos 8 critérios. */
function inventoryCsv(rows: AuditRow[]): string {
  const header = [
    'Aplicação',
    'Identificador',
    'Tipo',
    'Situação',
    'Visibilidade',
    'Criticidade',
    'Time',
    'Responsável',
    'Projeto',
    'Chamado',
    ...COMPLIANCE_ORDER.map((k) => COMPLIANCE_LABELS[k]),
    'Critérios atendidos',
    'Total de critérios',
    'Cobertura (%)',
    'Classificação',
  ];
  const body = rows.map(({ app, result }) => [
    app.name,
    app.slug,
    APP_TYPES[app.type]?.label ?? app.type,
    STATUS_TYPES[app.status]?.label ?? app.status,
    VISIBILITY_LABELS[app.visibility] ?? app.visibility,
    CRITICALITY_LABELS[app.criticality] ?? app.criticality,
    app.team,
    app.owner,
    app.project ?? '',
    app.ticket && app.ticket !== '—' ? app.ticket : '',
    ...result.checks.map((c) => CHECK_STATE_LABELS[complianceCheckState(c)]),
    result.ok,
    result.total,
    csvNumber(result.pct),
    COMPLIANCE_LEVEL_LABELS[result.level],
  ]);
  return toCsv([header, ...body]);
}

/** Uma linha por critério não atendido — o plano de remediação em CSV. */
function gapsCsv(rows: AuditRow[]): string {
  const header = [
    'Aplicação',
    'Identificador',
    'Tipo',
    'Visibilidade',
    'Criticidade',
    'Time',
    'Responsável',
    'Critério pendente',
    'Critérios atendidos',
    'Cobertura (%)',
    'Classificação',
  ];
  const body = rows.flatMap(({ app, result }) =>
    result.missing.map((c) => [
      app.name,
      app.slug,
      APP_TYPES[app.type]?.label ?? app.type,
      VISIBILITY_LABELS[app.visibility] ?? app.visibility,
      CRITICALITY_LABELS[app.criticality] ?? app.criticality,
      app.team,
      app.owner,
      c.label,
      result.ok,
      csvNumber(result.pct),
      COMPLIANCE_LEVEL_LABELS[result.level],
    ]),
  );
  return toCsv([header, ...body]);
}

/** Um responsável do cadastro com o total de pendências que respondem por ele. */
interface OwnerGaps {
  owner: string;
  teams: Set<string>;
  apps: number;
  gaps: number;
}

/**
 * Agrega as pendências por responsável e devolve os cinco com mais critérios em
 * falta. Só usa o que está cadastrado: quem não tem responsável cai num balde
 * explícito, e app sem time não inventa time.
 */
function topOwnersWithGaps(rows: AuditRow[]): OwnerGaps[] {
  const map = new Map<string, OwnerGaps>();
  for (const { app, result } of rows) {
    if (result.missing.length === 0) continue;
    const key = app.owner || 'Sem responsável atribuído';
    const entry = map.get(key) ?? { owner: key, teams: new Set<string>(), apps: 0, gaps: 0 };
    entry.apps += 1;
    entry.gaps += result.missing.length;
    if (app.team) entry.teams.add(app.team);
    map.set(key, entry);
  }
  return [...map.values()].sort((a, b) => b.gaps - a.gaps || b.apps - a.apps).slice(0, 5);
}

/** Iniciais do responsável — só o que já está cadastrado, nada inventado. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

function ArtifactRow({
  title,
  sub,
  dark,
  disabled,
  onDownload,
  last,
}: Readonly<{
  title: string;
  sub: string;
  dark: boolean;
  disabled?: boolean;
  onDownload: () => void;
  last?: boolean;
}>) {
  const p = makePalette(dark);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 0',
        borderBottom: last ? 'none' : `1px solid ${p.border}`,
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <div
        style={{
          ...rotuloCaixaAlta,
          width: 32,
          height: 32,
          borderRadius: 6,
          background: p.surface2,
          color: p.textMuted,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        CSV
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: p.text }}>{title}</div>
        <div style={{ ...numeroTabular, fontSize: 10.5, color: p.muted }}>{sub}</div>
      </div>
      {!disabled && (
        <ButtonGhost dark={dark} onClick={onDownload}>
          <IconArrow size={13} style={{ transform: 'rotate(90deg)' }} /> Baixar
        </ButtonGhost>
      )}
    </div>
  );
}

/** Uma linha da grade "Cobertura por requisito": rótulo, contagem e barra. */
function CoverageRequirementRow({ r, dark }: Readonly<{ r: ComplianceCoverage; dark: boolean }>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const color = complianceColor(r.pct);
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: byViewport(vp, { mobile: 8, tablet: 0, desktop: 0 }),
          marginBottom: 6,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12.5,
            fontWeight: 500,
            color: p.text,
            // "Levantamento de requisitos" ao lado da fração em 262px de coluna
            // só cabe se o rótulo puder quebrar — sem isto ele empurra a fração
            // para fora do cartão.
            minWidth: vp.compact ? 0 : undefined,
          }}
        >
          {r.label}
        </div>
        <div
          style={{
            ...numeroTabular,
            fontSize: 12,
            fontWeight: 600,
            color: p.text,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {r.count}
          <span style={{ color: p.muted }}>/{r.total}</span>
          <span
            style={{
              marginLeft: 10,
              color,
              fontSize: 11,
            }}
          >
            {r.pct.toFixed(0)}%
          </span>
        </div>
      </div>
      <CoverageBar value={r.count} total={r.total} color={color} dark={dark} height={6} />
      {r.exemption && (
        <div
          style={{
            fontSize: 10,
            color: p.muted,
            marginTop: 5,
            lineHeight: 1.4,
          }}
        >
          {r.exemption}
          {r.exempt > 0 && (
            <span style={{ ...numeroTabular }}>
              {' · '}
              {r.exempt} dispensada{r.exempt > 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Geometria responsiva da tabela de atenção ────────────────────────────────

/**
 * Rolagem lateral DELIBERADA da grade no tablet. Não é estouro: é a mesma saída
 * já validada no inventário e no acervo — comprimir seis colunas em 768px apaga
 * o nome da aplicação, e virar card já em 1024px jogaria fora a leitura em
 * tabela que o auditor usa para comparar aplicações linha a linha.
 * `scrollbarWidth: thin` para a barra não roubar altura das linhas.
 */
const TABLE_SCROLLER: CSSProperties = { overflowX: 'auto', scrollbarWidth: 'thin' };

/**
 * Piso de largura da linha quando ela rola de lado (tablet). A conta da grade:
 * 200px da coluna de cobertura + 70px de vãos (5 × 14px) = 270px que não
 * encolhem, e as outras cinco colunas somam 5,9fr. Com 1000px sobram 730px
 * fracionários — 297px para o nome da aplicação, 111px para cada selo, 124px
 * para o responsável e 87px para as pendências (as duas últimas medidas
 * conferidas no navegador a 768px). Em 768px SEM piso a linha teria 466px
 * fracionários: 56px por selo, menos do que "CRÍTICA" ocupa, e o cabeçalho
 * "VISIBILIDADE" quebraria em duas linhas.
 */
const ATTENTION_MIN_WIDTH = 1000;

/**
 * Geometria da linha por faixa. Um objeto por faixa, e não um ternário em cada
 * propriedade do JSX: são nove decisões na mesma linha, o bastante para estourar
 * o teto de complexidade cognitiva do componente. `slot` posiciona cada célula;
 * na faixa larga fica vazio e a grade de seis colunas segue o fluxo natural,
 * exatamente como antes.
 */
interface AttentionLayout {
  columns: string;
  gap: number;
  padding: string;
  align: CSSProperties['alignItems'];
  slot: Record<
    'app' | 'criticality' | 'visibility' | 'owner' | 'coverage' | 'missing',
    CSSProperties
  >;
  /** Rótulo antes do valor: só no card, onde não existe cabeçalho de coluna. */
  labels: boolean;
  missingAlign: CSSProperties['textAlign'];
}

const ATTENTION_WIDE: AttentionLayout = {
  columns: '2.4fr 0.9fr 0.9fr 1fr 200px 0.7fr',
  gap: 14,
  padding: '12px 0',
  align: 'center',
  slot: { app: {}, criticality: {}, visibility: {}, owner: {}, coverage: {}, missing: {} },
  labels: false,
  missingAlign: 'right',
};

/**
 * Card do celular: nome na 1ª linha, criticidade e visibilidade lado a lado na
 * 2ª, responsável na 3ª, cobertura na 4ª e as pendências na 5ª. As colunas são
 * `minmax(0, 1fr)` porque `1fr` puro tem piso de conteúdo — era ele que fazia a
 * linha esticar 337px além da tela em 375px (medido pela sonda).
 */
const ATTENTION_NARROW: AttentionLayout = {
  columns: 'minmax(0, 1fr) minmax(0, 1fr)',
  gap: 10,
  padding: '14px 0',
  align: 'start',
  slot: {
    app: { gridColumn: '1 / span 2', gridRow: 1 },
    criticality: { gridColumn: 1, gridRow: 2 },
    visibility: { gridColumn: 2, gridRow: 2 },
    owner: { gridColumn: '1 / span 2', gridRow: 3 },
    coverage: { gridColumn: '1 / span 2', gridRow: 4 },
    missing: { gridColumn: '1 / span 2', gridRow: 5 },
  },
  labels: true,
  missingAlign: 'left',
};

/**
 * Rótulo do valor no card do celular. Na grade quem nomeia a coluna é o
 * cabeçalho; sem ele, "PAULO ARRUDA GOMES" e "6/8 requisitos" soltos num card
 * não dizem de que coluna vieram.
 */
function CellLabel({ text, dark, show }: Readonly<{ text: string; dark: boolean; show: boolean }>) {
  const p = makePalette(dark);
  if (!show) return null;
  return (
    <div
      style={{
        ...rotuloCaixaAlta,
        fontSize: 9,
        fontWeight: 700,
        color: p.muted,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        marginBottom: 3,
      }}
    >
      {text}
    </div>
  );
}

/** Uma linha de "Aplicações que requerem atenção" (a mais fraca primeiro). */
function AttentionRow({
  app: a,
  result,
  dark,
  last,
}: Readonly<{ app: App; result: ComplianceResult; dark: boolean; last: boolean }>) {
  const p = makePalette(dark);
  const vp = useViewport();
  // No TABLET a linha continua sendo a grade larga, dentro do rolador lateral do
  // painel; só o celular troca de formato.
  const L = vp.mobile ? ATTENTION_NARROW : ATTENTION_WIDE;
  const color = complianceColor(result.pct);
  const missing = result.missing.length;
  // Rótulo pronto antes do JSX (o plural é o único ramo aqui dentro).
  const missingLabel = `${missing} pendente${missing > 1 ? 's' : ''}`;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: L.columns,
        gap: L.gap,
        padding: L.padding,
        alignItems: L.align,
        borderBottom: last ? 'none' : `1px solid ${p.border}`,
        fontSize: 12.5,
        // O piso só existe no tablet, onde a grade rola de lado. Em `0` (celular
        // e desktop) a propriedade é o próprio padrão de um bloco: nada muda.
        minWidth: byViewport(vp, { mobile: 0, tablet: ATTENTION_MIN_WIDTH, desktop: 0 }),
      }}
    >
      <div style={{ ...L.slot.app, display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <AppPhoto app={a} size={28} dark={dark} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: p.text }}>{a.name}</div>
          {/* Mono só quando HÁ código de projeto (`PRJ-TI-XXX`, identificador).
              O fallback "Sem projeto" é prosa: em mono lia como se "Sem
              projeto" fosse o próprio código. */}
          <div
            style={{
              fontSize: 10,
              color: p.muted,
              fontFamily: a.project ? fontMono : fontSans,
            }}
          >
            {a.project || 'Sem projeto'}
          </div>
        </div>
      </div>
      <div style={L.slot.criticality}>
        <CellLabel text="Criticidade" dark={dark} show={L.labels} />
        <CriticalityBadge level={a.criticality} dark={dark} />
      </div>
      <div style={L.slot.visibility}>
        <CellLabel text="Visibilidade" dark={dark} show={L.labels} />
        <VisibilityBadge visibility={a.visibility} dark={dark} />
      </div>
      <div style={{ ...L.slot.owner, fontSize: 12, color: p.text, minWidth: 0 }}>
        <CellLabel text="Responsável" dark={dark} show={L.labels} />
        {a.owner}
      </div>
      <div style={L.slot.coverage}>
        <CellLabel text="Cobertura" dark={dark} show={L.labels} />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 4,
          }}
        >
          <div style={{ flex: 1 }}>
            <CoverageBar value={result.ok} total={result.total} color={color} dark={dark} />
          </div>
          <div
            style={{
              ...numeroTabular,
              fontSize: 11,
              fontWeight: 700,
              color,
              width: 38,
              textAlign: 'right',
            }}
          >
            {result.pct.toFixed(0)}%
          </div>
        </div>
        <div
          style={{
            ...numeroTabular,
            fontSize: 10,
            color: p.muted,
          }}
        >
          {result.ok}/{result.total} requisitos
        </div>
      </div>
      <div
        style={{
          ...L.slot.missing,
          ...numeroTabular,
          textAlign: L.missingAlign,
          fontSize: 12,
          fontWeight: 600,
          color: missing > 0 ? '#dc2626' : '#16a34a',
        }}
      >
        <CellLabel text="Pendências" dark={dark} show={L.labels} />
        {missing > 0 ? missingLabel : 'Nenhum'}
      </div>
    </div>
  );
}

/** Uma linha de "Responsáveis com pendências": iniciais, times e o total. */
function OwnerGapRow({
  o,
  dark,
  accent,
  last,
}: Readonly<{ o: OwnerGaps; dark: boolean; accent: string; last: boolean }>) {
  const p = makePalette(dark);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '32px 1fr auto',
        gap: 12,
        alignItems: 'center',
        paddingBottom: last ? 0 : 14,
        borderBottom: last ? 'none' : `1px solid ${p.border}`,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 16,
          background: dark ? `${accent}22` : `${accent}15`,
          color: accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        {initialsOf(o.owner)}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: p.text }}>{o.owner}</div>
        <div style={{ fontSize: 11, color: p.muted, fontFamily: fontSans }}>
          {[...o.teams].join(' · ') || 'Sem time cadastrado'}
        </div>
      </div>
      <span
        style={{
          ...numeroTabular,
          fontSize: 10,
          fontWeight: 700,
          padding: '3px 8px',
          borderRadius: 3,
          background: dark ? '#dc262622' : '#fee2e2',
          color: '#dc2626',
          letterSpacing: '0.08em',
          whiteSpace: 'nowrap',
        }}
      >
        {o.gaps} em {o.apps} app{o.apps > 1 ? 's' : ''}
      </span>
    </div>
  );
}

/**
 * Um dos três indicadores do topo (críticas, LGPD, produção). Virou componente
 * para carregar as próprias decisões de faixa: em 320px a coluna tem 143px, e o
 * número de 36px com o rótulo em caixa alta e 0,1em de espaçamento não caberia —
 * era daí que saía parte dos 92 estouros medidos.
 */
function KpiCard({
  label,
  value,
  sub,
  color,
  icon,
  dark,
}: Readonly<{
  label: string;
  value: number;
  sub: string;
  color: string;
  icon: ReactNode;
  dark: boolean;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  return (
    <div
      style={{
        background: p.surface,
        border: `1px solid ${p.border}`,
        borderRadius: 8,
        padding: byViewport(vp, { mobile: 14, tablet: 16, desktop: 18 }),
        // Sem `minWidth: 0` o piso de conteúdo do cartão estica a coluna e a
        // fileira inteira passa da borda, mesmo com as trilhas em `minmax(0,1fr)`.
        minWidth: vp.compact ? 0 : undefined,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: byViewport(vp, { mobile: 9.5, tablet: 10, desktop: 10 }),
          fontWeight: 700,
          color: p.muted,
          textTransform: 'uppercase',
          // "Trata dados pessoais" com 0,1em de espaçamento não fecha em duas
          // linhas de 143px; com 0,06em fecha.
          letterSpacing: byViewport(vp, { mobile: '0.06em', tablet: '0.1em', desktop: '0.1em' }),
          marginBottom: byViewport(vp, { mobile: 8, tablet: 10, desktop: 10 }),
          overflowWrap: vp.compact ? 'anywhere' : 'normal',
        }}
      >
        <span style={{ color, display: 'flex', flexShrink: 0 }}>{icon}</span>
        {label}
      </div>
      <div
        style={{
          ...numeroTabular,
          fontSize: byViewport(vp, { mobile: 28, tablet: 32, desktop: 36 }),
          fontWeight: 600,
          color: p.text,
          letterSpacing: '-0.025em',
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 11,
          color: p.muted,
          marginTop: 6,
          overflowWrap: vp.compact ? 'anywhere' : 'normal',
        }}
      >
        {sub}
      </div>
    </div>
  );
}

export function ScreenAudit({
  dark,
  accent = NOC_RED,
  apps,
  onRefresh,
  refreshing = false,
  dataUpdatedAt,
}: Readonly<
  ScreenProps & {
    apps: App[];
    /** Recarrega a lista de aplicações. Sem callback, o botão não é renderizado. */
    onRefresh?: () => void;
    refreshing?: boolean;
    /** Epoch (ms) em que a lista chegou ao cliente — ex.: `dataUpdatedAt` do react-query. */
    dataUpdatedAt?: number;
  }
>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const navigate = useNavigate();

  // Uma passada de conformidade por app; tudo abaixo deriva daqui. No mesmo memo
  // vem o momento em que ESTES dados chegaram ao cliente: as duas coisas saem da
  // MESMA lista, e sem `dataUpdatedAt` o instante é o do render desta lista —
  // jamais uma data fixa. (Separado, o memo da data teria `apps` nas
  // dependências sem usá-la no cálculo.)
  const { rows, loadedAt } = useMemo<{ rows: AuditRow[]; loadedAt: Date }>(
    () => ({
      rows: apps.map((a) => ({ app: a, result: complianceOf(a) })),
      loadedAt: dataUpdatedAt ? new Date(dataUpdatedAt) : new Date(),
    }),
    [apps, dataUpdatedAt],
  );
  const coverage = useMemo(() => complianceCoverage(apps), [apps]);

  const fullyCompliant = rows.filter((r) => r.result.level === 'total').length;
  const partial = rows.filter((r) => r.result.level === 'parcial').length;
  const failing = rows.filter((r) => r.result.level === 'falha').length;
  const gapsCount = rows.reduce((acc, r) => acc + r.result.missing.length, 0);

  const loadedAtLabel = loadedAt.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const stamp = fileStamp(new Date());
  const exportInventory = () =>
    downloadCsv(`master-apps_auditoria_${stamp}.csv`, inventoryCsv(rows));
  const exportGaps = () => downloadCsv(`master-apps_pendencias_${stamp}.csv`, gapsCsv(rows));

  // Responsáveis das aplicações com critérios em falta (dado do cadastro).
  const owners = useMemo(() => topOwnersWithGaps(rows), [rows]);

  // Resumo do CSV de pendências, montado fora do JSX (o plural é o único ramo).
  const gapsSummary = `${gapsCount} linha${gapsCount === 1 ? '' : 's'} · uma por critério em falta`;

  // As seis aplicações com menos critérios atendidos abrem o plano de ação.
  const attention = [...rows].sort((a, b) => a.result.ok - b.result.ok).slice(0, 6);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        fontFamily: fontSans,
        color: p.text,
        background: p.bg,
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <Sidebar active="audit" dark={dark} accent={accent} role="admin" />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TopBar
          dark={dark}
          title="Auditoria 2026"
          subtitle="Cobertura documental e artefatos para o relatório de auditoria"
          right={
            <>
              <span
                style={{
                  ...numeroTabular,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  color: p.muted,
                }}
              >
                <IconClock size={12} /> Dados carregados às {loadedAtLabel}
              </span>
              <ButtonGhost dark={dark} onClick={() => navigate('/admin/audit/criteria')}>
                <IconShield size={13} /> Ver critérios
              </ButtonGhost>
              {onRefresh && (
                <ButtonGhost dark={dark} onClick={onRefresh}>
                  <IconRefresh size={13} /> {refreshing ? 'Atualizando' : 'Atualizar'}
                </ButtonGhost>
              )}
              <button
                type="button"
                onClick={exportInventory}
                style={{
                  // 40px no toque: é o piso de alvo que a sonda cobra, e exportar
                  // o CSV é justamente o botão que se erra com o dedo.
                  height: byViewport(vp, { mobile: 40, tablet: 34, desktop: 34 }),
                  padding: '0 14px',
                  background: '#0a0a0b',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 7,
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: fontSans,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <IconArrow size={13} style={{ transform: 'rotate(90deg)' }} /> Exportar CSV
              </button>
            </>
          }
        />

        <div
          style={{
            flex: 1,
            // 24px de folga de cada lado em 320px de tela são 15% da largura
            // útil — largura que os cartões precisam mais do que a margem.
            padding: byViewport(vp, { mobile: 12, tablet: 16, desktop: 24 }),
            overflow: 'auto',
            minHeight: 0,
          }}
        >
          {/* Linha de resumo em destaque · 4 colunas no desktop, 2 abaixo dele.
              Com `minmax(0, 1fr)` porque `1fr` puro tem piso de conteúdo: era a
              fileira de indicadores que esticava 463px além da tela em 375px. */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: byViewport(vp, {
                mobile: 'repeat(2, minmax(0, 1fr))',
                tablet: 'repeat(2, minmax(0, 1fr))',
                desktop: '1.4fr 1fr 1fr 1fr',
              }),
              gap: byViewport(vp, { mobile: 10, tablet: 12, desktop: 14 }),
              marginBottom: byViewport(vp, { mobile: 14, tablet: 16, desktop: 18 }),
            }}
          >
            {/* O cartão do rosco ocupa a fileira inteira fora do desktop: em meia
                largura o "1 apps em conformidade total" ficava com 100px e a
                palavra "conformidade" vazava do cartão. */}
            <Card
              dark={dark}
              padding={byViewport(vp, { mobile: 14, tablet: 18, desktop: 20 })}
              style={{
                gridColumn: byViewport(vp, {
                  mobile: 'span 2',
                  tablet: 'span 2',
                  desktop: 'auto',
                }),
              }}
            >
              <div
                style={{
                  display: 'flex',
                  // No celular o rosco vai ACIMA da legenda: lado a lado sobravam
                  // 100px para as três faixas de classificação.
                  flexDirection: byViewport(vp, {
                    mobile: 'column',
                    tablet: 'row',
                    desktop: 'row',
                  }),
                  alignItems: byViewport(vp, {
                    mobile: 'stretch',
                    tablet: 'center',
                    desktop: 'center',
                  }),
                  gap: byViewport(vp, { mobile: 14, tablet: 20, desktop: 24 }),
                }}
              >
                <AuditDonut
                  ok={fullyCompliant}
                  total={apps.length}
                  dark={dark}
                  size={byViewport(vp, { mobile: 140, tablet: 160, desktop: 160 })}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: p.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.12em',
                      marginBottom: 6,
                    }}
                  >
                    Conformidade global
                  </div>
                  <div
                    style={{
                      fontSize: byViewport(vp, { mobile: 18, tablet: 22, desktop: 22 }),
                      fontWeight: 600,
                      color: p.text,
                      letterSpacing: '-0.02em',
                      marginBottom: 10,
                      // "conformidade" com 22px pede 145px de largura: mais do que
                      // sobra num cartão de 254px depois do rosco.
                      overflowWrap: vp.mobile ? 'anywhere' : 'normal',
                    }}
                  >
                    {fullyCompliant} apps em conformidade total
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                        <span
                          style={{ width: 8, height: 8, borderRadius: 2, background: '#16a34a' }}
                        />{' '}
                        Conformidade total
                      </span>
                      <span style={{ ...numeroTabular, fontWeight: 600 }}>{fullyCompliant}</span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                        <span
                          style={{ width: 8, height: 8, borderRadius: 2, background: '#ca8a04' }}
                        />{' '}
                        Parcial (5-7/8)
                      </span>
                      <span style={{ ...numeroTabular, fontWeight: 600 }}>{partial}</span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                        <span
                          style={{ width: 8, height: 8, borderRadius: 2, background: '#dc2626' }}
                        />{' '}
                        Não conforme
                      </span>
                      <span style={{ ...numeroTabular, fontWeight: 600 }}>{failing}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {[
              {
                l: 'Apps críticas',
                v: apps.filter((a) => a.criticality === 'alta').length,
                sub: 'classificação alta',
                color: '#dc2626',
                icon: <IconAlert size={14} />,
              },
              {
                l: 'Trata dados pessoais',
                v: apps.filter((a) => a.lgpd).length,
                sub: 'declarado no cadastro',
                color: '#2563eb',
                icon: <IconShield size={14} />,
              },
              {
                l: 'Em produção',
                v: apps.filter((a) => a.status === 'prod').length,
                sub: 'situação em produção',
                color: '#16a34a',
                icon: <IconActivity size={14} />,
              },
            ].map((k) => (
              <KpiCard
                key={k.l}
                label={k.l}
                value={k.v}
                sub={k.sub}
                color={k.color}
                icon={k.icon}
                dark={dark}
              />
            ))}
          </div>

          {/* Cobertura por requisito */}
          <Card
            dark={dark}
            title="Cobertura por requisito"
            subtitle="Apps que atendem cada critério · dispensas contam como atendidas"
            action={<Tag dark={dark}>{apps.length} apps</Tag>}
            style={{ marginBottom: byViewport(vp, { mobile: 14, tablet: 18, desktop: 18 }) }}
          >
            {/* Uma coluna no celular: em duas, "Levantamento de requisitos" e a
                fração dividiam 143px e a linha estourava 33px (medido). */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: byViewport(vp, {
                  mobile: 'minmax(0, 1fr)',
                  tablet: 'repeat(2, minmax(0, 1fr))',
                  desktop: '1fr 1fr',
                }),
                gap: byViewport(vp, { mobile: '12px', tablet: '14px 24px', desktop: '14px 36px' }),
              }}
            >
              {coverage.map((r) => (
                <CoverageRequirementRow key={r.key} r={r} dark={dark} />
              ))}
            </div>
          </Card>

          {/* Apps que requerem atenção */}
          <Card
            dark={dark}
            title="Aplicações que requerem atenção"
            subtitle="Ordenadas por cobertura · prioridade no plano de remediação"
            action={
              <Tag dark={dark} tone="red">
                para o plano de ação
              </Tag>
            }
            padding={0}
          >
            {/* No TABLET este bloco é o rolador lateral da grade (rolagem
                deliberada, com o piso de ATTENTION_MIN_WIDTH nas linhas). No
                celular e no desktop ele é só o recuo interno de antes. */}
            <div
              style={{
                padding: byViewport(vp, {
                  mobile: '4px 12px 12px',
                  tablet: '8px 16px 16px',
                  desktop: '8px 16px 16px',
                }),
                ...byViewport<CSSProperties>(vp, {
                  mobile: {},
                  tablet: TABLE_SCROLLER,
                  desktop: {},
                }),
              }}
            >
              {/* Cabeçalho de colunas só existe onde HÁ colunas: no card do
                  celular cada valor carrega o próprio rótulo (ver CellLabel). */}
              <div
                style={{
                  ...rotuloCaixaAlta,
                  display: byViewport(vp, { mobile: 'none', tablet: 'grid', desktop: 'grid' }),
                  gridTemplateColumns: ATTENTION_WIDE.columns,
                  gap: 14,
                  padding: '10px 0',
                  minWidth: byViewport(vp, { mobile: 0, tablet: ATTENTION_MIN_WIDTH, desktop: 0 }),
                  fontSize: 10,
                  fontWeight: 700,
                  color: p.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  borderBottom: `1px solid ${p.border}`,
                }}
              >
                <div>Aplicação</div>
                <div>Criticidade</div>
                <div>Visibilidade</div>
                <div>Responsável</div>
                <div>Cobertura</div>
                <div style={{ textAlign: 'right' }}>Pendências</div>
              </div>

              {attention.map(({ app: a, result }, i: number, arr) => (
                <AttentionRow
                  key={a.id}
                  app={a}
                  result={result}
                  dark={dark}
                  last={i === arr.length - 1}
                />
              ))}
            </div>
          </Card>

          {/* Rodapé: artefatos gerados agora + responsáveis com pendências.
              Empilha no celular — os dois painéis em 143px cada deixariam o
              "Baixar" sem largura ao lado do rótulo do artefato. */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: byViewport(vp, {
                mobile: 'minmax(0, 1fr)',
                tablet: 'repeat(2, minmax(0, 1fr))',
                desktop: '1fr 1fr',
              }),
              gap: byViewport(vp, { mobile: 12, tablet: 14, desktop: 14 }),
              marginTop: byViewport(vp, { mobile: 14, tablet: 18, desktop: 18 }),
            }}
          >
            <Card
              dark={dark}
              title="Artefatos para a auditoria"
              subtitle="Gerados no navegador, a partir das aplicações carregadas"
              action={<Tag dark={dark}>sob demanda</Tag>}
            >
              <div>
                <ArtifactRow
                  dark={dark}
                  title="Inventário com cobertura documental"
                  sub={`${rows.length} linha${rows.length === 1 ? '' : 's'} · 8 critérios + cobertura e classificação`}
                  onDownload={exportInventory}
                  disabled={rows.length === 0}
                />
                <ArtifactRow
                  dark={dark}
                  title="Pendências por aplicação"
                  sub={gapsCount > 0 ? gapsSummary : 'Nenhuma pendência registrada no inventário'}
                  onDownload={exportGaps}
                  disabled={gapsCount === 0}
                  last
                />
              </div>
              <div
                style={{
                  marginTop: 10,
                  paddingTop: 10,
                  borderTop: `1px solid ${p.border}`,
                  fontSize: 10.5,
                  color: p.muted,
                  lineHeight: 1.5,
                }}
              >
                Arquivos CSV com separador ponto e vírgula e codificação UTF-8, prontos para abrir
                no Excel em português. Nada é armazenado no servidor.
              </div>
            </Card>

            <Card
              dark={dark}
              title="Responsáveis com pendências"
              subtitle="Quem responde pelas aplicações com critérios em falta"
              action={
                <Tag dark={dark} tone={gapsCount > 0 ? 'red' : 'neutral'}>
                  {gapsCount} pendência{gapsCount === 1 ? '' : 's'}
                </Tag>
              }
            >
              {owners.length === 0 ? (
                <div
                  style={{
                    fontSize: 12.5,
                    color: p.muted,
                    lineHeight: 1.6,
                    padding: '6px 0',
                  }}
                >
                  Nenhuma pendência: todas as aplicações carregadas atendem aos 8 critérios.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {owners.map((o, i: number, arr) => (
                    <OwnerGapRow
                      key={o.owner}
                      o={o}
                      dark={dark}
                      accent={accent}
                      last={i === arr.length - 1}
                    />
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
